import styled from 'styled-components';

// Dark theme values based on the screenshot - refined
const themes = {
  dark: {
    bg: '#121724',
    cardBg: '#1A1F32',
    contentBg: '#1E2235',
    text: '#ffffff',
    textSecondary: '#9EA4B8',
    headerBorder: '#2A2F42',
    link: '#9D86FF',
    inputBg: '#262B3D',
    highlight: '#262B3D', // Slightly darker
    aiMessageBg: '#262B3D',
    userMessageBg: 'transparent'
  },
  purple: '#8850FF',
  purpleLight: '#9D86FF',
  purpleDark: '#6B35F9',
  cyan: '#03dac6',
  green: '#00c853',
  blue: '#4D8DFF', // Adjusted to match screenshot
  darkBlue: '#0039cb'
};

// Using dark theme as default
const currentTheme = 'dark';

export const Container = styled.div`
  background-color: ${themes[currentTheme].cardBg};
  color: ${themes[currentTheme].text};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%; /* Restore original */
  min-height: 400px; /* Restore original */
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  border: 1px solid ${themes[currentTheme].headerBorder};
  
  /* Important: Prevent component from unmounting during critical operations */
  transform: translateZ(0); /* Force GPU acceleration */
  will-change: transform; /* Hint for browser optimization */
  
  @media (max-width: 599px) {
    min-height: 300px; /* Restore original */
  }
`;

export const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: ${themes[currentTheme].text};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

export const Content = styled.div`
  flex: 1;
  height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background-color: ${themes[currentTheme].cardBg};
  padding-top: ${props => props.noHeader ? '8px' : '0'};
`;

export const TranscriptContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  scrollbar-width: thin;
  scrollbar-color: ${themes[currentTheme].headerBorder} ${themes[currentTheme].cardBg};
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${themes[currentTheme].cardBg};
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${themes[currentTheme].headerBorder};
    border-radius: 6px;
  }
`;

export const MainLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

export const TranscriptSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 0;
`;

// Ensure FooterBar is active and correctly styled
export const FooterBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch; 
  width: 100%; 
`;

export const ControlsContainer = styled.div`
  padding: 8px 16px;
  border-top: 1px solid ${themes[currentTheme].headerBorder};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${themes[currentTheme].bg};
  min-height: 40px;
`;

export const Button = styled.button`
  background-color: ${props => props.primary ? themes.purple : 'transparent'};
  color: ${props => props.primary ? '#ffffff' : themes[currentTheme].text};
  border: ${props => props.primary ? 'none' : `1px solid ${themes[currentTheme].headerBorder}`};
  border-radius: 32px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  
  &:hover {
    background-color: ${props => props.primary ? themes.purpleDark : themes[currentTheme].highlight};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: 8px;
  }
`;

export const AudioVisualizerContainer = styled.div`
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${themes[currentTheme].bg};
  border-radius: 8px;
  margin: 0 24px 8px;
  padding: 2px 12px;
`;

export const AudioVisualizerCompact = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${themes[currentTheme].bg};
  border-radius: 8px;
  padding: 8px 12px;
  margin: 0 6px 0 20px;
  width: 140px;
  height: 100%;
`;

export const StatusIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => {
    switch(props.status) {
      case 'connected': return themes.green;
      case 'connecting': return themes.blue;
      case 'error': return '#ff5252';
      default: return themes[currentTheme].textSecondary;
    }
  }};
  margin-right: 8px;
`;

export const StatusText = styled.span`
  font-size: 14px;
  color: ${themes[currentTheme].textSecondary};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

export const StatusContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const ErrorMessage = styled.div`
  background-color: rgba(255, 82, 82, 0.1);
  color: #ff5252;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 24px;
  font-size: 14px;
  border: 1px solid rgba(255, 82, 82, 0.3);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

// Transcript styles
export const Transcript = styled.ul`
  margin: 0;
  padding: 0;
  width: 100%;
  list-style-type: none;
`;

export const Line = styled.li`
  color: ${({ type }) => {
    if (type === 'error') return '#ff5252';
    if (type === 'log') return themes[currentTheme].textSecondary;
    return themes[currentTheme].text;
  }};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  width: 100%;
  text-align: left;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0;
  white-space: pre-line;
  margin-bottom: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: ${({ type }) => {
    if (['ai', 'ai-log'].includes(type)) return themes[currentTheme].aiMessageBg;
    return themes[currentTheme].userMessageBg;
  }};
`;

export const Root = styled.span`
  display: inline;
  white-space: pre;
  font-weight: 600;
  margin-right: 6px;
  color: ${({ type }) => {
    if (['ai', 'ai-log'].includes(type)) return themes.purpleLight;
    if (['human', 'human-log'].includes(type)) return themes.blue;
    return themes[currentTheme].textSecondary;
  }};
`;

// Audio controls styles
export const AudioControlsContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

export const ControlButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const IconButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${props => props.active ? themes.purple : themes[currentTheme].inputBg};
  color: ${props => props.active ? '#ffffff' : themes[currentTheme].text};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? themes.purpleDark : themes[currentTheme].headerBorder};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const MicIconButton = styled(IconButton)`
  width: 42px;
  height: 42px;
`;

export const EndCallButton = styled(Button)`
  min-width: 120px;
  border-radius: 24px;
  padding: 10px 20px;
  background-color: transparent;
  border: 1px solid ${themes[currentTheme].headerBorder};
  font-weight: 500;
  
  svg {
    margin-right: 8px;
  }
`;

// Audio visualizer styles
export const VisualizerBars = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  height: 24px;
  width: 100%;
  padding: 0 4px;
`;

export const VisualizerBar = styled.div.attrs(props => ({
  style: {
    height: `${props.height}%`,
  },
}))`
  width: 2px;
  background-color: ${props => props.speaker === 'user' ? themes.blue : themes.purpleLight};
  border-radius: 1px;
  /* transition: height 0.05s linear; */ /* Smoother but can be CPU intensive with rapid updates */
  /* A very short transition can smooth out rapid changes if desired */
  /* Consider removing if audio level updates are already frequent (e.g., every 80-100ms) */
`;

export const SpeakerLabel = styled.div`
  font-size: 10px;
  color: ${themes[currentTheme].textSecondary};
  margin: 0 6px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  white-space: nowrap;
  font-weight: 500;
  min-width: 22px;
`;

// New styles for initial view
export const CenteredContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 60px;
  height: 100%;
  flex-direction: column;
  padding-left: 20px;
  padding-right: 20px;
  text-align: center;
  background-color: ${themes[currentTheme].cardBg};
  z-index: 5;
  position: relative;
`;

export const IntroText = styled.p`
  color: ${themes[currentTheme].textSecondary};
  font-size: 16px;
  margin: 0;
  width: 100%;
  line-height: 1.6;
  padding: 12px 24px;
  border-radius: 8px;
  background-color: rgba(18, 23, 36, 0.8);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);
`;

export const LargeButton = styled(Button)`
  padding: 14px 32px;
  font-size: 16px;
  border-radius: 40px;
  box-shadow: 0 4px 12px rgba(136, 80, 255, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(136, 80, 255, 0.4);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(136, 80, 255, 0.3);
  }
  
  svg {
    width: 20px;
    height: 20px;
    margin-right: 10px;
  }
`;

export const PlayButtonOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 80px;
  background-color: rgba(18, 23, 36, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
  pointer-events: auto;
  
  &:hover {
    background-color: rgba(18, 23, 36, 0.7);
  }
`;

export const PlayButton = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${themes.purple};
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 8px 24px rgba(136, 80, 255, 0.4);
  transition: all 0.3s ease;
  
  svg {
    width: 40px;
    height: 40px;
    fill: white;
    margin-left: 5px;
  }
  
  ${PlayButtonOverlay}:hover & {
    transform: scale(1.1);
    background-color: ${themes.purpleDark};
  }
`; 